import React, { useEffect } from "react";
import { View, StyleSheet, ActivityIndicator, Platform } from "react-native";
import { MotiView } from "moti";
import { FileMagnifyingGlassIcon, CheckCircleIcon, ClockIcon } from "@phosphor-icons/react";
import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CourseEvaluationCardContainer from "../../containers/adminDashboard/CourseEvaluationCardContainer/CourseEvaluationCardContainer";
import CourseInfoCard from "../../components/adminDashboard/CourseInfoCard/CourseInfoCard";
import { useGetAllCoursesBySchoolQuery, Course } from "../../graphql/generated/graphql";
import { useAppConfig } from "../../AppConfigProvider";

function PageHeader() {
  return (
    <MotiView
      from={{ opacity: 0, translateY: 20 }}
      animate={{ opacity: 1, translateY: 0 }}
      style={styles.headerTextContainer}>
      <CustomText
        text="Course Modernizer"
        size="m"
        textType="display"
        weight="ultraBold"
        style={{ color: "#101828" }}
        role="heading"
        aria-level="1"
      />

      <CustomSpacing type="vertical" size="s" />

      <CustomText
        text="Modernize your LMS courses with AI-powered analysis and evidence-based improvement recommendations"
        size="m"
        weight="medium"
        style={{ color: "#667085" }}
        role="heading"
        aria-level="2"
      />
    </MotiView>
  );
}

function CourseEvaluatorView() {
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const {
    data: coursesData,
    loading: loadingCourses,
    refetch,
    startPolling,
    stopPolling,
  } = useGetAllCoursesBySchoolQuery({
    variables: { schoolId, page: 1, pageSize: 50 },
    skip: !schoolId,
    fetchPolicy: "network-only",
  });

  const courses: Course[] = (coursesData?.getAllCoursesBySchool?.data?.courses as Course[]) || [];

  // Calculate statistics
  const totalCourses = courses.length;
  const evaluatedCourses = courses.filter((course) => {
    const latestEvaluation = course.evaluations
      ?.filter((evaluation) => evaluation.status === "completed" && evaluation.createdAt)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    return !!latestEvaluation;
  }).length;
  const pendingEvaluation = totalCourses - evaluatedCourses;

  // Check if there are any pending evaluations
  const hasPendingEvaluations = courses.some((course) =>
    course.evaluations?.some((evaluation) => evaluation.status === "pending")
  );

  // Poll when there are pending evaluations
  useEffect(() => {
    if (hasPendingEvaluations) {
      startPolling(2000);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [hasPendingEvaluations, startPolling, stopPolling]);

  if (loadingCourses) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.contentContainer}>
            <View style={styles.headerContainer}>
              <PageHeader />
            </View>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        </View>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <View style={styles.headerContainer}>
            <PageHeader />
          </View>
          <CustomSpacing type="vertical" size="l" />

          <View style={styles.infoCardsContainer}>
            <CourseInfoCard
              icon={<FileMagnifyingGlassIcon />}
              number={totalCourses}
              title="Total Courses"
              iconBackgroundColor="rgb(239, 246, 255)"
              iconColor="rgb(37, 99, 235)"
              borderColor="rgb(234, 236, 240)"
            />
            <CourseInfoCard
              icon={<CheckCircleIcon />}
              number={evaluatedCourses}
              title="Evaluated"
              iconBackgroundColor="rgb(240, 253, 244)"
              iconColor="rgb(22, 163, 74)"
              borderColor="rgb(187, 247, 208)"
            />
            <CourseInfoCard
              icon={<ClockIcon />}
              number={pendingEvaluation}
              title="Pending Evaluation"
              iconBackgroundColor="rgb(255, 251, 235)"
              iconColor="rgb(217, 119, 6)"
              borderColor="rgb(253, 230, 138)"
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <CustomText
            text="Your LMS Courses"
            size="s"
            weight="medium"
            style={styles.headerThird}
            role="heading"
            aria-level="3"
          />
          <CustomSpacing type="vertical" size="l" />
          <CourseEvaluationCardContainer courses={courses} refetchCourses={refetch} />
        </View>
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  headerThird: {
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  infoCardsContainer: {
    flexDirection: "row",
    gap: 20,
    ...(Platform.OS === "web"
      ? ({
          display: "flex",
        } as any)
      : {}),
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
    ...(Platform.OS === "web"
      ? ({
          display: "flex",
        } as any)
      : {}),
  },
  headerTextContainer: {
    flex: 1,
  },
});

export default CourseEvaluatorView;
